import React from 'react';
import { ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';

/**
 * OrgZoomControls - the floating zoom pill for the org chart canvas: `-`,
 * the current percentage, `+`, and Fit. Self-positioned (`absolute
 * bottom-4 right-4`) - the caller renders it as a direct child of the
 * canvas's `relative` shell, before the scrollable canvas element itself so
 * it sits earlier in tab order despite being painted on top of it (see
 * OrgChart.js). 16px inset clears a classic OS scrollbar (15-17px) so the
 * pill never sits on top of one.
 *
 * Exact testids/aria kept stable across the toolbar -> canvas-overlay move
 * (rounds 3-4) so e2e specs and screen readers see no change:
 * `org-zoom-out`/`org-zoom-level`/`org-zoom-in`/`org-zoom-fit`.
 */
const OrgZoomControls = ({ zoom, zoomIn, zoomOut, fit }) => (
  <div
    className="absolute bottom-4 right-4 z-10 flex items-center gap-1 rounded-full border
      border-gray-300 bg-white px-1.5 py-1 shadow-lg"
  >
    <button
      type="button"
      data-testid="org-zoom-out"
      aria-label="Zoom out"
      onClick={zoomOut}
      className="flex h-[38px] w-[38px] items-center justify-center rounded-full
        text-gray-600 hover:bg-gray-100"
    >
      <ZoomOut size={15} />
    </button>
    <span
      data-testid="org-zoom-level"
      aria-live="polite"
      className="w-11 text-center text-xs font-medium text-gray-600"
    >
      {Math.round(zoom * 100)}%
    </span>
    <button
      type="button"
      data-testid="org-zoom-in"
      aria-label="Zoom in"
      onClick={zoomIn}
      className="flex h-[38px] w-[38px] items-center justify-center rounded-full
        text-gray-600 hover:bg-gray-100"
    >
      <ZoomIn size={15} />
    </button>
    <button
      type="button"
      data-testid="org-zoom-fit"
      aria-label="Fit to screen"
      onClick={fit}
      className="ml-1 inline-flex h-[38px] items-center gap-1.5 rounded-full px-3
        text-sm font-medium text-gray-700 hover:bg-gray-100"
    >
      <Maximize2 size={14} /> Fit
    </button>
  </div>
);

export default OrgZoomControls;
